import { createContext, useContext } from 'react';
import { configure } from 'mobx';
import { GeoStore } from './domain/geoStore';
import { DomainStore } from './domain/domainStore';
import { CartStore } from './domain/cartStore';
import { GroupStore } from './domain/groupStore';
import { BrowseInfoStore } from './ui-stores/browse-info/browseInfoStore';
import { RateSegmentsStore } from './ui-stores/rate-segments/rateSegmentsStore';
import { PopularPlansStore } from './ui-stores/popular-plans/popularPlansStore';
import { ComparisonStore } from './ui-stores/comparison/comparisonStore';
import { BrowsingCriteriaModalStore } from './ui-stores/browsing/browsingCriteriaModalStore';
import { BrowsingFilterStore } from './ui-stores/browsing/browsingFilterStore';
import { BrowsingStore } from './ui-stores/browsing/browsingStore';
import { FormStore } from './domain/formStore';
import { ProposalCartStore } from './ui-stores/cart/proposalCartStore';
import { ContactMeModalStore } from './general/contactMeModalStore';
import { EnrollmentStore } from './domain/enrollmentStore';
import { QuoteModalStore } from './ui-stores/cart/quoteModalStore';
import { HelpMeDecideStore } from './ui-stores/help-me-decide/helpMeDecideStore';
import { BrowsingPortfolioConfigModalStore } from './ui-stores/comparison/browsingPortfolioConfigModalStore';
import { EnrollModalStore } from './ui-stores/cart/enrollModalStore';
import { UserStore } from './domain/userStore';
import { NotificationModalStore } from './general/notificationModalStore';

configure({
  useProxies: 'never',
  computedRequiresReaction: true,
  reactionRequiresObservable: true,
  observableRequiresReaction: true,
});

export class RootStore {
  // Domain stores
  geoStore: GeoStore;
  domainStore: DomainStore;
  cartStore: CartStore;
  groupStore: GroupStore;
  formStore: FormStore;
  enrollmentStore: EnrollmentStore;
  userStore: UserStore;

  // UI stores
  browseInfoStore: BrowseInfoStore;
  rateSegmentsStore: RateSegmentsStore;
  popularPlansStore: PopularPlansStore;
  comparisonStore: ComparisonStore;
  browsingStore: BrowsingStore;
  browsingFilterStore: BrowsingFilterStore;
  browsingCriteriaModalStore: BrowsingCriteriaModalStore;
  proposalCartStore: ProposalCartStore;
  quoteModalStore: QuoteModalStore;
  helpMeDecideStore: HelpMeDecideStore;
  browsingPortfolioConfigModalStore: BrowsingPortfolioConfigModalStore;
  enrollModalStore: EnrollModalStore;

  // General stores
  contactMeModalStore: ContactMeModalStore;
  notificationModalStore: NotificationModalStore;

  constructor() {
    // Domain stores
    this.geoStore = new GeoStore();
    this.domainStore = new DomainStore();
    this.cartStore = new CartStore();
    this.groupStore = new GroupStore();
    this.formStore = new FormStore();
    this.enrollmentStore = new EnrollmentStore();
    this.userStore = new UserStore();

    // UI stores
    this.browseInfoStore = new BrowseInfoStore(this);
    this.rateSegmentsStore = new RateSegmentsStore(this);
    this.popularPlansStore = new PopularPlansStore(this);
    this.comparisonStore = new ComparisonStore(this);
    this.browsingStore = new BrowsingStore(this);
    this.browsingFilterStore = new BrowsingFilterStore(this);
    this.browsingCriteriaModalStore = new BrowsingCriteriaModalStore(this);
    this.proposalCartStore = new ProposalCartStore(this);
    this.quoteModalStore = new QuoteModalStore(this);
    this.helpMeDecideStore = new HelpMeDecideStore(this);
    this.browsingPortfolioConfigModalStore = new BrowsingPortfolioConfigModalStore(this);
    this.enrollModalStore = new EnrollModalStore(this);

    // General stores
    this.contactMeModalStore = new ContactMeModalStore(this);
    this.notificationModalStore = new NotificationModalStore(this);
  }
}

const rootStore = new RootStore();

export function getRootStoreInstance(): RootStore {
  return rootStore;
}

const RootContext = createContext(rootStore);

export const useRootStore = (): RootStore => {
  return useContext(RootContext);
};

export { RootContext };
